import { contactFieldKey } from './contactFieldKey';

/**
 * Turning a parsed spreadsheet into campaign recipients.
 *
 * <p>ONE copy, deliberately. This mapping is reached from two screens — the new
 * campaign builder, and "add a whole file" on a standing campaign — and the two
 * had already drifted within a day of the second one existing: the builder
 * normalised the number and kept the name and the chosen columns on the person,
 * while the bulk add sent the raw cell and the numbered slots alone. The same
 * file dropped on the two screens produced different contacts, and only one of
 * them left an agent anything to greet the person by.
 */

/**
 * The number as WhatsApp needs it.
 *
 * A bare 10-digit cell is Indian and a 12-digit one starting 91 already carries
 * the country code; anything else is trusted as typed, because guessing a
 * country for it is how a message goes to a different continent.
 */
export const normalizePhone = (raw: string): string => {
  let d = (raw || '').replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('91')) return '+' + d;
  if (d.length === 10) return '+91' + d;
  if ((raw || '').trim().startsWith('+')) return '+' + d;
  return d ? '+' + d : '';
};

/** Which column is which, as the operator mapped them. */
export interface RecipientMapping {
  headers: string[];
  /** The column holding the number. */
  phoneCol: number;
  /** The template's {{n}} slots, in body order. */
  slots: string[];
  /** slot -> column index. A slot left unmapped is filled by the campaign's own defaults. */
  varMap: Record<string, number>;
  /** The column that holds their name, or -1. */
  nameCol: number;
  /** Columns worth keeping on the person. */
  fieldCols: number[];
}

/**
 * One row's variables: the template's slots, plus what is true about the person.
 *
 * The contact mapping is ADDITIVE. The numbered keys stay exactly as they are,
 * because that is what fills the template — Meta's body component is built from
 * numeric keys only, ascending — so the named ones are ignored by the send and
 * read only when the contact is created or enriched.
 */
export const buildVariables = (row: string[], m: RecipientMapping): Record<string, string> => {
  const v: Record<string, string> = {};
  m.slots.forEach((tok) => {
    const col = m.varMap[tok];
    if (col != null && col >= 0) v[tok] = row[col] ?? '';
  });
  if (m.nameCol >= 0 && (row[m.nameCol] ?? '').trim()) v.name = row[m.nameCol].trim();
  m.fieldCols.forEach((col) => {
    const key = contactFieldKey(m.headers[col], col);
    const value = (row[col] ?? '').trim();
    if (key && value && v[key] === undefined) v[key] = value;
  });
  return v;
};

/**
 * Every row that names somebody reachable.
 *
 * A number under 8 digits is a spreadsheet artefact — a serial, a blank cell
 * read as 0 — and sending to it burns the template's quality rating against a
 * number nobody holds.
 */
export const buildRecipients = (rows: string[][], m: RecipientMapping) =>
  rows
    .map((r) => ({ phone: normalizePhone(r[m.phoneCol] || ''), variables: buildVariables(r, m) }))
    .filter((r) => r.phone && r.phone.length >= 8);
